import { ParsedJob, SlicerFlavor } from './types';

const FILAMENT_DENSITY: Record<string, number> = {
  PLA: 1.24, PETG: 1.27, ABS: 1.04, PC: 1.20, TPU: 1.21, ASA: 1.07, NYLON: 1.14,
};
const DEFAULT_DENSITY = 1.24;
const FILAMENT_DIAMETER_MM = 1.75;

const minutesFromDhms = (s: string): number => {
  let total = 0;
  const m = s.match(/(?:(\d+)d)?\s*(?:(\d+)h)?\s*(?:(\d+)m)?\s*(?:(\d+(?:\.\d+)?)s)?/);
  if (!m) return 0;
  total += (parseInt(m[1] || '0', 10)) * 24 * 60;
  total += (parseInt(m[2] || '0', 10)) * 60;
  total += (parseInt(m[3] || '0', 10));
  total += Math.floor(parseFloat(m[4] || '0') / 60);
  return total;
};

const toHM = (mins: number) => ({ hours: Math.floor(mins / 60), minutes: mins % 60 });

const sumFloats = (raw: string): number =>
  raw.split(/[;,]/).map(s => parseFloat(s.trim())).filter(n => !isNaN(n)).reduce((a, b) => a + b, 0);

const firstMatch = (text: string, re: RegExp): string | null => {
  const m = text.match(re);
  return m ? m[1].trim() : null;
};

const stripQuotes = (s: string) => s.replace(/^"(.*)"$/, '$1');

export interface SlicerProfile {
  flavor: SlicerFlavor;
  detect: (head: string) => boolean;
  extract: (text: string) => Omit<ParsedJob, 'flavor'>;
}

const prusaLike = (flavor: SlicerFlavor, signature: RegExp): SlicerProfile => ({
  flavor,
  detect: (head) => signature.test(head),
  extract: (text) => {
    const timeStr = firstMatch(text, /;\s*estimated printing time \(normal mode\)\s*=\s*(.+)/i);
    const weightStr = firstMatch(text, /;\s*filament used \[g\]\s*=\s*(.+)/i);
    const type = firstMatch(text, /;\s*filament_type\s*=\s*(.+)/i);
    const printer = firstMatch(text, /;\s*printer_model\s*=\s*(.+)/i);
    const totalMins = timeStr ? minutesFromDhms(timeStr) : null;
    return {
      printTime: totalMins != null ? toHM(totalMins) : null,
      filamentWeightGrams: weightStr ? sumFloats(weightStr) : null,
      filamentWeightEstimated: false,
      filamentType: type ? type.split(/[;,]/)[0].trim() : null,
      printerModel: printer,
      thumbnailDataUrl: null,
    };
  },
});

const bambu: SlicerProfile = {
  flavor: 'BambuStudio',
  detect: (head) => /^; HEADER_BLOCK_START/m.test(head) || /generated by BambuStudio/i.test(head),
  extract: (text) => {
    const timeStr = firstMatch(text, /;\s*total estimated time:\s*(.+)/i);
    const weightStr = firstMatch(text, /;\s*total filament weight \[g\]:\s*(.+)/i);
    const type = firstMatch(text, /;\s*filament_settings_id\s*=\s*(.+)/i);
    const printer = firstMatch(text, /;\s*printer_model\s*=\s*(.+)/i);
    const totalMins = timeStr ? minutesFromDhms(timeStr) : null;
    return {
      printTime: totalMins != null ? toHM(totalMins) : null,
      filamentWeightGrams: weightStr ? parseFloat(weightStr) : null,
      filamentWeightEstimated: false,
      filamentType: type ? stripQuotes(type.split(/[;,]/)[0].trim()) : null,
      printerModel: printer,
      thumbnailDataUrl: null,
    };
  },
};

const cura: SlicerProfile = {
  flavor: 'Cura',
  detect: (head) => /^;Generated with Cura_SteamEngine/m.test(head) || /^;FLAVOR:/m.test(head),
  extract: (text) => {
    const timeS = firstMatch(text, /;TIME:(\d+)/);
    const weightDirect = firstMatch(text, /;Filament weight:([\d.]+)/i);
    const lengthM = firstMatch(text, /;Filament used:\s*([\d.]+)m/i);
    const type = firstMatch(text, /;Filament type:(.+)/i);
    const printer = firstMatch(text, /;TARGET_MACHINE\.NAME:(.+)/i);

    const totalMins = timeS ? Math.floor(parseInt(timeS, 10) / 60) : null;

    let weight: number | null = null;
    let estimated = false;
    if (weightDirect) {
      weight = parseFloat(weightDirect);
    } else if (lengthM) {
      const lenMm = parseFloat(lengthM) * 1000;
      const radius = FILAMENT_DIAMETER_MM / 2;
      const volumeCm3 = (Math.PI * radius * radius * lenMm) / 1000;
      const density = (type && FILAMENT_DENSITY[type.toUpperCase().trim()]) || DEFAULT_DENSITY;
      weight = volumeCm3 * density;
      estimated = true;
    }

    return {
      printTime: totalMins != null ? toHM(totalMins) : null,
      filamentWeightGrams: weight,
      filamentWeightEstimated: estimated,
      filamentType: type ? type.trim() : null,
      printerModel: printer,
      thumbnailDataUrl: null,
    };
  },
};

export const profiles: SlicerProfile[] = [
  bambu, // most specific signature first
  prusaLike('OrcaSlicer', /generated by OrcaSlicer/i),
  prusaLike('SuperSlicer', /generated by SuperSlicer/i),
  prusaLike('PrusaSlicer', /generated by PrusaSlicer/i),
  cura,
];
